"use client";

import { Button } from "@cmp-src/primitives/button";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface MapFrameProps {
  height: number;
  /** Changes when the content's natural size may have changed (map loaded). */
  contentKey: string;
  children: React.ReactNode;
  onMouseMove?: (x: number, y: number) => void;
  overlay?: React.ReactNode;
  /** The map's natural size once known, for overlays drawn in its coordinates. */
  onContentSize?: (size: { w: number; h: number }) => void;
  /**
   * Shift+drag draws a box instead of panning; the box arrives in the
   * map's own coordinates once the mouse is released.
   */
  onBoxSelect?: (box: { x: number; y: number; w: number; h: number }) => void;
}

type Box = { x0: number; y0: number; x1: number; y1: number };

type View = { x: number; y: number; s: number };

const MIN_SCALE = 0.05;
const MAX_SCALE = 8;

/** Natural size of the map inside: the SVG's viewBox, else its box. */
const contentSize = (inner: HTMLElement): { w: number; h: number } | null => {
  const svg = inner.querySelector("svg");
  if (!svg) {
    return null;
  }
  const vb = svg.viewBox.baseVal;
  if (vb && vb.width > 0 && vb.height > 0) {
    // Pin the SVG to its own coordinate size so the frame's scale is the
    // only scaling at play.
    svg.setAttribute("width", String(vb.width));
    svg.setAttribute("height", String(vb.height));
    svg.style.width = `${vb.width}px`;
    svg.style.height = `${vb.height}px`;
    return { w: vb.width, h: vb.height };
  }
  const box = svg.getBoundingClientRect();
  return box.width > 0 ? { w: box.width, h: box.height } : null;
};

/**
 * A fixed-height viewport with wheel zoom (around the cursor), drag pan,
 * and a fit-to-frame that runs once the map inside has rendered.
 */
export const MapFrame: React.FC<MapFrameProps> = ({
  height,
  contentKey,
  children,
  onMouseMove,
  overlay,
  onContentSize,
  onBoxSelect
}) => {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<View>({ x: 0, y: 0, s: 1 });
  const drag = useRef<{ x: number; y: number; vx: number; vy: number } | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  // A selection box in the frame's pixels while Shift is held.
  const [box, setBox] = useState<Box | null>(null);

  const fit = useCallback(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) {
      return;
    }
    const size = contentSize(inner);
    if (!size) {
      return;
    }
    onContentSize?.(size);
    const s =
      Math.min(outer.clientWidth / size.w, outer.clientHeight / size.h) * 0.96;
    setView({
      s,
      x: (outer.clientWidth - size.w * s) / 2,
      y: (outer.clientHeight - size.h * s) / 2
    });
  }, [onContentSize]);

  // The map inside renders asynchronously (SVG injection, viewer mount):
  // try to fit a few times after the content key changes.
  useEffect(() => {
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts++;
      const inner = innerRef.current;
      if (inner && contentSize(inner)) {
        fit();
        window.clearInterval(timer);
      } else if (attempts > 40) {
        window.clearInterval(timer);
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [contentKey, fit]);

  const zoomAt = (factor: number, cx: number, cy: number) => {
    setView((v) => {
      const s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.s * factor));
      const k = s / v.s;
      return { s, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k };
    });
  };

  // React's wheel listener is passive; a native one can stop the page scroll.
  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) {
      return;
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = outer.getBoundingClientRect();
      zoomAt(
        e.deltaY < 0 ? 1.15 : 1 / 1.15,
        e.clientX - rect.left,
        e.clientY - rect.top
      );
    };
    outer.addEventListener("wheel", onWheel, { passive: false });
    return () => outer.removeEventListener("wheel", onWheel);
  }, []);

  const center = () => {
    const outer = outerRef.current;
    return outer
      ? { x: outer.clientWidth / 2, y: outer.clientHeight / 2 }
      : { x: 0, y: 0 };
  };

  return (
    <div
      ref={outerRef}
      className="relative select-none overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
      // The frame's height is the tile's choice.
      style={{ height, cursor: isDragging ? "grabbing" : "grab" }}
      onMouseDown={(e) => {
        if (e.shiftKey && onBoxSelect) {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          setBox({ x0: x, y0: y, x1: x, y1: y });
          return;
        }
        drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
        setIsDragging(true);
      }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        onMouseMove?.(e.clientX - rect.left, e.clientY - rect.top);
        if (box) {
          setBox({
            ...box,
            x1: e.clientX - rect.left,
            y1: e.clientY - rect.top
          });
          return;
        }
        if (drag.current) {
          const d = drag.current;
          setView((v) => ({
            ...v,
            x: d.vx + (e.clientX - d.x),
            y: d.vy + (e.clientY - d.y)
          }));
        }
      }}
      onMouseUp={() => {
        if (box && onBoxSelect) {
          const left = Math.min(box.x0, box.x1);
          const top = Math.min(box.y0, box.y1);
          const w = Math.abs(box.x1 - box.x0);
          const h = Math.abs(box.y1 - box.y0);
          if (w > 3 && h > 3) {
            // Frame pixels back to map units through the pan/zoom transform.
            onBoxSelect({
              x: (left - view.x) / view.s,
              y: (top - view.y) / view.s,
              w: w / view.s,
              h: h / view.s
            });
          }
        }
        setBox(null);
        drag.current = null;
        setIsDragging(false);
      }}
      onMouseLeave={() => {
        setBox(null);
        drag.current = null;
        setIsDragging(false);
      }}
      onDoubleClick={fit}
    >
      <div
        ref={innerRef}
        className="absolute top-0 left-0 origin-top-left"
        // The pan/zoom transform.
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`
        }}
      >
        {children}
      </div>
      {overlay}
      {box ? (
        <div
          className="pointer-events-none absolute border border-primary border-dashed bg-primary/10"
          style={{
            left: Math.min(box.x0, box.x1),
            top: Math.min(box.y0, box.y1),
            width: Math.abs(box.x1 - box.x0),
            height: Math.abs(box.y1 - box.y0)
          }}
        />
      ) : null}
      <div className="absolute top-2 right-2 flex flex-col gap-1">
        <Button
          variant="outline"
          size="sm"
          aria-label="Zoom in"
          onClick={() => {
            const c = center();
            zoomAt(1.3, c.x, c.y);
          }}
        >
          +
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-label="Zoom out"
          onClick={() => {
            const c = center();
            zoomAt(1 / 1.3, c.x, c.y);
          }}
        >
          −
        </Button>
        <Button variant="outline" size="sm" aria-label="Fit" onClick={fit}>
          ⤢
        </Button>
      </div>
    </div>
  );
};
